/**
 * React Query hooks for Pipeline operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import pipelineService from '../services/pipeline.service';

interface UsePipelineListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const usePipelineListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 10,
  sortBy = 'created_at',
  order = 'desc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UsePipelineListQueryParams) => {
  return useQuery({
    queryKey: ['pipelines', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await pipelineService.listPipelines(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UsePipelineQueryParams {
  pipelineId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const usePipelineQuery = ({
  pipelineId,
  workspaceId,
  idToken,
  enabled = true,
}: UsePipelineQueryParams) => {
  return useQuery({
    queryKey: ['pipeline', pipelineId],
    queryFn: async () => {
      if (!pipelineId || !workspaceId || !idToken) {
        throw new Error('Pipeline ID, Workspace ID and token are required');
      }

      return await pipelineService.getPipeline(pipelineId, workspaceId, idToken);
    },
    enabled: enabled && !!pipelineId && !!workspaceId && !!idToken,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UsePipelineRoundsQueryParams {
  pipelineId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const usePipelineRoundsQuery = ({
  pipelineId,
  workspaceId,
  idToken,
  enabled = true,
}: UsePipelineRoundsQueryParams) => {
  return useQuery({
    queryKey: ['pipeline-rounds', pipelineId],
    queryFn: async () => {
      if (!pipelineId || !workspaceId || !idToken) {
        throw new Error('Missing required parameters');
      }
      const pipeline = await pipelineService.getPipeline(
        pipelineId,
        workspaceId,
        idToken
      );
      return pipeline.rounds; // Returns PipelineRound[] sorted by order
    },
    enabled: enabled && !!pipelineId && !!workspaceId && !!idToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
};
